import { Box, Typography, styled } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { ErrorMessage } from "@outschool/ui-components-shared";
import {
  Loading,
  TrackingPageSection,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import React, { useCallback, useMemo } from "react";

import logo from "../../images/Outschool_Logo_Color.svg";
import { classRecordingStatusQuery } from "../graphql/queries/ClassRecording";
import { isClassRecordingType } from "../hooks";
import OutschoolVideoJsAdapter from "./VideoJs/OutschoolVideoJsAdapter";

import type {
  ClassRecordingStatusQuery,
  ClassRecordingStatusQueryVariables,
} from "..";

function useClassRecordingStatusQuery(
  sectionUid: string,
  recordingUid: string,
) {
  const { error, data } = useQueryWithPreviousData<
    ClassRecordingStatusQuery,
    ClassRecordingStatusQueryVariables
  >(classRecordingStatusQuery, {
    fetchPolicy: "cache-and-network",
    variables: {
      sectionUid,
      recordingUid,
    },
  });

  const recording = data?.section?.classRecording;
  const readyToPlay = isClassRecordingType(recording) && !!recording?.playUrl;
  const speakingTimes = isClassRecordingType(recording)
    ? recording.speakingTimes ?? null
    : null;

  return {
    statusError: error,
    playUrl: recording?.playUrl,
    readyToPlay,
    speakingTimes,
  };
}

const StyledImage = styled("img")({});

export default function ClassRecordingsPlayer({
  sectionUid,
  recordingUid,
  learnerUids,
  handlePlayClick,
}: {
  sectionUid: string;
  recordingUid: string;
  learnerUids?: string[] | null;
  handlePlayClick?: () => void;
}) {
  const { t } = useTranslation(
    "ui-components-classroom\\ClassRecordingsPlayer",
  );

  const { statusError, playUrl, readyToPlay, speakingTimes } =
    useClassRecordingStatusQuery(sectionUid, recordingUid);

  const markers = useMemo(() => {
    if (speakingTimes == null || learnerUids == null || !learnerUids?.length) {
      return [];
    }
    return speakingTimes.flatMap(entry => {
      const uid = entry.learnerSpeaker?.uid;
      if (uid == null || !learnerUids.includes(uid)) {
        return [];
      }
      return entry.speakingTimeMarkers;
    });
  }, [speakingTimes, learnerUids]);

  const [playButtonNode, setPlayButtonNode] = React.useState<SVGElement>();

  const { trackTouch: videoPlayTouch } = useImpressionTracking({
    node: playButtonNode,
    trackingLabel: "playVideo",
    uniqueId: recordingUid,
  });

  const onPlayClick = () => {
    handlePlayClick?.();
    videoPlayTouch({ clicked: true });
  };

  const onVideoError = useCallback((error: Error) => {
    OsPlatform.captureError(error, {
      tags: {
        package: "ui-components-classroom",
        s3_video_playback_error: true,
      },
    });
  }, []);

  if (statusError) {
    return <ErrorMessage value={statusError} />;
  }

  if (readyToPlay) {
    return (
      <TrackingPageSection name="s3Recording" uniqueId="s3Recording">
        {refNode => (
          <OutschoolVideoJsAdapter
            markers={markers}
            trackingRef={refNode}
            src={playUrl!}
            title={t("Class Video Recording")}
            splash={
              <StyledImage
                src={logo}
                // eslint-disable-next-line i18next/no-literal-string
                alt={"Outschool"}
                sx={{ width: "75%", padding: "1em" }}
              />
            }
            playButtonRef={(ref: SVGElement) => setPlayButtonNode(ref)}
            onPlayClick={onPlayClick}
            onVideoError={onVideoError}
            errorMessage={
              <>
                {t("Sorry, that didn’t work. We’re looking into it.")}
                <br />
                {t("You can reload the page or contact support@outschool.com.")}
              </>
            }
          />
        )}
      </TrackingPageSection>
    );
  }

  // When loading, show loader with same layout as <Video/> to match size and aspect ratio
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1024px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
      }}
    >
      <Typography variant="body1">
        {t(
          "One moment - we’re processing the recording. New recordings may take up to four hours after the class ends to process.",
        )}
      </Typography>
      <Loading />
    </Box>
  );
}
